import { lazy, useCallback, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { AuthenticationResult, InteractionStatus } from '@azure/msal-browser';

import { LoginPage, RedirectToPortal } from '@/modules/authentication/components';
import { ModalManager } from '@common/components/ModalManager';
import { Lazy } from './modules/common/components/Lazy/Lazy';
import { theme } from './modules/common/theme';
import { FloorplanAdmin } from './modules/floorplanAdmin';
import { useApp } from '@/modules/core/hooks/useApp';
import { useConfig } from '@/modules/common/hooks/useConfig';
import { Box } from '@mui/system';

const Workspace = lazy(() => import('./modules/core/components/DigitalTwin'));

const DigitalTwin = (
  <Lazy key='Workspace'>
    <Box>
      <Workspace />
      <ModalManager />
    </Box>
  </Lazy>
);

const FloorPlanAdmin = (
  <Lazy key='FloorplanAdmin'>
    <Box>
      <FloorplanAdmin />
      <ModalManager />
    </Box>
  </Lazy>
);

export function usePreventBrowserZoom(): void {
  useEffect(() => {
    const preventWheelZoom = (event: WheelEvent) => {
      if (!event.ctrlKey) return;

      event.preventDefault();
    };

    document.addEventListener('wheel', preventWheelZoom, { passive: false });

    return () => {
      document.removeEventListener('wheel', preventWheelZoom);
    };
  }, []);
}

export function App() {
  useApp();
  usePreventBrowserZoom();

  const { app } = useConfig();
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, instance, accounts } = useMsal();

  const setActiveAccountIfNeeded = useCallback(
    (result?: AuthenticationResult) => {
      if (!isAuthenticated) return;

      if (result?.account) {
        instance.setActiveAccount(result.account);
        return;
      }

      if (!instance.getActiveAccount() && accounts.length > 0) {
        instance.setActiveAccount(accounts[0]);
      }
    },
    [accounts, instance, isAuthenticated],
  );

  useEffect(() => {
    setActiveAccountIfNeeded();
  }, [setActiveAccountIfNeeded]);

  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return null;
  }

  return (
    <>
      <CssBaseline />
      <ThemeProvider theme={theme}>
        <Routes>
          {app.env === 'production' && (
            <Route
              path='/'
              element={
                inProgress === InteractionStatus.None ? <RedirectToPortal forceRedirect /> : null
              }
            />
          )}

          {!isAuthenticated &&
            (inProgress === InteractionStatus.None ||
              inProgress === InteractionStatus.SsoSilent) && (
              <Route path='*' element={<LoginPage />} />
            )}

          {isAuthenticated && (
            <Route>
              <Route path='/:projectId'>
                <Route path='' element={FloorPlanAdmin} />
                <Route path='builder/:alternativeId/version/:revisionId' element={DigitalTwin} />
                <Route
                  path='builder/:alternativeId/version/:revisionId/viewer'
                  element={DigitalTwin}
                />
                <Route
                  path='builder/:alternativeId/version/:revisionId/visualization/:simulationId/:simRevisionId?'
                  element={DigitalTwin}
                />
                <Route path='*' element={<Navigate to='' replace />} />
              </Route>

              <Route path='*' element={<RedirectToPortal />} />
            </Route>
          )}
        </Routes>
      </ThemeProvider>
    </>
  );
}
